/* SupportChat — floating chat widget that connects to
 * `POST /api/support/chat`. The agent knows the whole game (rarity,
 * PvP, credits, tiers, marketplace) and holds session context so
 * follow-ups make sense.
 *
 * UX: fixed launcher pill in the bottom-right; expands into a
 * conversation panel. Mobile: full-height sheet. Desktop: card.
 */
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, X, Sparkles, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const STORAGE_KEY = 'wq_support_session';

const STARTER_PROMPTS = [
  'What is a Discovery Score?',
  'How do rarities work?',
  'How does PvP work?',
  'How do I get more credits?',
];

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);   // {role:'user'|'assistant', text}
  const [session, setSession] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', text: msg }]);
    setBusy(true);
    try {
      const r = await api.post('/support/chat', { message: msg, session_id: session });
      if (r.data.session_id && r.data.session_id !== session) {
        setSession(r.data.session_id);
        try { localStorage.setItem(STORAGE_KEY, r.data.session_id); } catch { /* silent */ }
      }
      setMessages(m => [...m, { role: 'assistant', text: r.data.reply || '…' }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'The support agent is offline.';
      setMessages(m => [...m, { role: 'assistant', text: `⚠️ ${detail}` }]);
    } finally { setBusy(false); }
  };

  const reset = () => {
    setMessages([]); setSession(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* silent */ }
  };

  return (
    <>
      {/* Floating launcher — always available inside AppShell. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="support-chat-launcher"
        aria-label="Open support chat"
        className="fixed right-4 z-40 h-12 w-12 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 text-black shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform border-2 border-white/20"
        style={{ bottom: 'calc(6rem + env(safe-area-inset-bottom))' }}
      >
        <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-60" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-300 border border-black/40" />
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
            onClick={() => setOpen(false)}
            data-testid="support-chat-modal"
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full sm:max-w-md h-[85vh] sm:h-[600px] rounded-t-3xl sm:rounded-3xl bg-[#0f1a15] border border-white/10 flex flex-col overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center gap-2 p-4 border-b border-white/10 bg-gradient-to-b from-emerald-900/30 to-transparent">
                <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-emerald-300" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-300">Support · Live</p>
                  <p className="font-serif text-lg leading-tight">WildQuest Guide</p>
                </div>
                {messages.length > 0 && (
                  <button type="button" onClick={reset} data-testid="support-reset"
                    className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-white">
                    Reset
                  </button>
                )}
                <button type="button" onClick={() => setOpen(false)} data-testid="support-close"
                  className="p-1.5 rounded-full hover:bg-white/5">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Messages */}
              <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 && (
                  <div className="text-center py-6 space-y-4">
                    <p className="text-sm text-white/80">
                      Hi! Ask me anything about WildQuest — rarity, credits, PvP, tiers, or how any feature works.
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {STARTER_PROMPTS.map((p) => (
                        <button key={p} type="button" onClick={() => send(p)}
                          data-testid={`support-starter-${p.slice(0,10).replace(/\s+/g,'-').toLowerCase()}`}
                          className="text-[10px] uppercase tracking-widest bg-white/5 border border-white/10 hover:border-emerald-400/40 hover:bg-emerald-500/5 rounded-full px-3 py-2 leading-tight">
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                       data-testid={`support-msg-${m.role}-${i}`}>
                    <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === 'user'
                        ? 'bg-emerald-500/15 border border-emerald-400/30 text-emerald-50'
                        : 'bg-white/5 border border-white/10 text-white/90'
                    }`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                {busy && (
                  <div className="flex justify-start">
                    <div className="bg-white/5 border border-white/10 rounded-2xl px-3 py-2 text-sm text-muted-foreground flex items-center gap-2"
                         data-testid="support-thinking">
                      <Loader2 className="w-3 h-3 animate-spin" /> Thinking…
                    </div>
                  </div>
                )}
              </div>

              {/* Composer */}
              <form onSubmit={(e) => { e.preventDefault(); send(); }}
                className="p-3 border-t border-white/10 flex items-center gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask anything about WildQuest…"
                  data-testid="support-input"
                  disabled={busy}
                  maxLength={1000}
                  className="flex-1 h-11 px-4 rounded-full bg-black/40 border border-white/10 text-sm outline-none focus:border-emerald-400/50 placeholder:text-muted-foreground/60"
                />
                <button type="submit" disabled={busy || !input.trim()} data-testid="support-send"
                  className="h-11 w-11 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 text-black flex items-center justify-center disabled:opacity-40">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
